// Retour haptique — Web Vibration API.
//
// Sert à confirmer une saisie sans lever les yeux : sur un tapis de practice,
// le joueur tape « réussi » en regardant sa balle, pas son écran. La vibration
// est la seule confirmation qu'il perçoive.
//
// ── Ce qui l'empêche de marcher, et qu'on ne peut pas deviner ───────────────
//
// L'API est absente d'iOS : Safari ne l'implémente pas, ni sur iPhone ni sur
// iPad, et aucun réglage n'y change quoi que ce soit. Là où elle existe
// (Android, Chrome, Firefox), elle peut encore refuser :
//
//   * navigator.vibrate() rend `false` quand le navigateur décline ;
//   * elle est ignorée sans rien dire si l'appel ne suit pas une interaction
//     de l'utilisateur, ou si l'onglet n'a pas le focus ;
//   * elle est ignorée si le téléphone est en mode silencieux avec vibration
//     coupée, ou en économie d'énergie — le navigateur rend alors `true`, et
//     rien ne se passe.
//
// Le dernier cas est indétectable depuis la page : c'est pourquoi les
// préférences proposent un bouton d'essai plutôt qu'un état affiché. Seul le
// poignet peut trancher.

import { useCallback } from 'react';
import { useSettings } from '../lib/settings.tsx';

export type HapticPattern = 'tap' | 'success' | 'error' | 'warning';

/** Durées en ms, par intensité (1 = discret, 3 = marqué). */
const PATTERNS: Record<HapticPattern, number[][]> = {
    tap: [[8], [15], [25]],
    success: [[12, 40, 12], [20, 50, 20], [30, 60, 30]],
    error: [[40, 60, 40], [60, 70, 60], [90, 80, 90]],
    warning: [[20, 80, 20], [35, 90, 35], [50, 100, 50]],
};

export const hapticsSupported = (): boolean =>
    typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

export type HapticTest =
    | { ok: true; note: string }
    | { ok: false; reason: string };

export function useHaptics() {
    const { settings } = useSettings();

    const fire = useCallback((pattern: number[]): boolean => {
        if (!hapticsSupported()) return false;
        try {
            return navigator.vibrate(pattern);
        } catch {
            // Certains navigateurs lèvent hors d'un geste utilisateur.
            return false;
        }
    }, []);

    const vibrate = useCallback(
        (pattern: HapticPattern = 'tap') => {
            if (!settings.hapticsEnabled) return;
            const level = Math.min(Math.max(settings.hapticsIntensity, 1), 3) - 1;
            fire(PATTERNS[pattern][level]!);
        },
        [settings.hapticsEnabled, settings.hapticsIntensity, fire],
    );

    /** Essai franc, pour l'écran de réglages.
     *
     *  Ne prétend jamais que la vibration a été ressentie : le navigateur peut
     *  accepter l'appel et le téléphone ne rien faire. Le message le dit. */
    const test = useCallback((): HapticTest => {
        if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
            return {
                ok: false,
                reason: "Ce navigateur n'implémente pas la vibration. C'est le cas de tous "
                    + "les navigateurs sur iPhone et iPad : la fonction ne peut pas y marcher, "
                    + "quel que soit le réglage.",
            };
        }
        // Motif volontairement long et net : un « tap » de 8 ms ne se sent pas
        // à travers une poche, et ferait conclure à tort que rien ne marche.
        const accepted = fire([120, 80, 120]);
        return accepted
            ? {
                ok: true,
                note: "Vibration envoyée. Si vous n'avez rien senti, le téléphone l'a "
                    + 'ignorée : vérifiez qu\'il n\'est pas en silencieux vibration coupée, '
                    + "ni en économie d'énergie.",
            }
            : {
                ok: false,
                reason: 'Le navigateur a refusé la vibration. Elle est parfois bloquée '
                    + "tant que la page n'a pas été touchée, ou dans un onglet en arrière-plan.",
            };
    }, [fire]);

    return { vibrate, test, supported: hapticsSupported() };
}
